import { DeleteOutlined, PlusOutlined, QuestionCircleTwoTone } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Descriptions, Form, Input, message, Popover, Row, Select, Spin, Typography } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useLoginModal } from '../../../context/loginModalContext'
import { useUser } from '../../../context/userContext'
import { showAuthError } from '../../../shared/utils/webHelpers'
import { StyledDrawer } from '../CommonStyles'
import ConfirmDeleteModal from '../ConfirmDeleteModal'
import StyledAppointmentTag from '../styles/StyledAppointmentTag'
import { StyledPopOverTagContent } from '../styles/StyledPopOverTagContent'
import { customFieldsPlaceHolder, customTypes } from './CustomFieldsHelpers'
import { DELETE_FIELD, EDIT_FIELDS } from '@dentolize/clinic-mobile/src/shared/store/mutations/companyMutations'
import { GET_FIELDS } from '@dentolize/clinic-mobile/src/shared/store/queries/companyQueries'
import { mainTwoToneColor } from '../../../variables'

let staticKey = ''
let staticFieldName = ''
let staticId = ''

const CustomFieldsDrawer = props => {
  const { childDrawerVisible, setChildDrawerVisible, parent, fieldsData, fieldsLoading, parentSetFields } = props

  const [deleting, setDeleting] = useState({})
  const [formVisible, setFormVisible] = useState(false)

  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { user } = useUser()

  const [editCompanyFields, { loading }] = useMutation(EDIT_FIELDS, {
    refetchQueries: [{ query: GET_FIELDS, variables: { parent } }],
    awaitRefetchQueries: true,
    update: handleUpdate
  })

  const [deleteCompanyField] = useMutation(DELETE_FIELD, {
    refetchQueries: [
      {
        query: GET_FIELDS,
        variables: { parent }
      }
    ]
  })

  const { validateFields, resetFields, getFieldValue } = form

  useEffect(() => {
    childDrawerVisible && resetFields()
  }, [childDrawerVisible])

  function handleUpdate(_, updatedFields) {
    updatedFields.data.editCompanyFields.map(updatedField => {
      if (!fieldsData.companyCustomFields.find(field => field.id === updatedField.id)) {
        parentSetFields([
          {
            name: ['customFields', updatedField.id, 'id'],
            value: updatedField.id
          }
        ])
        if (updatedField.type === 'Phone') {
          parentSetFields([
            updatedField.type === 'Phone' && {
              name: ['customFields', updatedField.id, 'value'],
              value: user.company.code
            }
          ])
        }
      }
      return true
    })
  }

  const handleSubmit = e => {
    e.preventDefault()
    validateFields().then(async values => {
      if (!values.fields?.length) {
        setChildDrawerVisible(false)
        return
      }

      if (values.fields.length > 20) return

      const fields = values.fields.map((field, order) => ({
        id: field.id || order + 1,
        name: field.name,
        type: field.type,
        required: field.required || false,
        parent,
        order
      }))

      try {
        await editCompanyFields({
          variables: { fields }
        })
        message.success(t('common.fieldUpdated'))
        setChildDrawerVisible(false)
      } catch (e) {
        showAuthError(e, t, setLoginModalVisible)
      }
    })
  }

  const removeField = (key, remove) => async fieldName => {
    staticKey = key
    staticId = getFieldValue(['fields', staticKey, 'id'])
    staticFieldName = getFieldValue(['fields', staticKey, 'name'])

    if (staticId) {
      if (fieldName !== staticFieldName) {
        setFormVisible(true)
        return
      }
      try {
        setDeleting({
          [staticKey]: true
        })
        await deleteCompanyField({
          variables: { field: staticId }
        })
        setDeleting({})
      } catch (e) {
        setDeleting({})
        message.error(t('common.somethingWentWrong'))
      }
    }
    remove(staticKey)
    staticKey = null
    staticId = null
    staticFieldName = null
  }

  const isDeleting = !!Object.keys(deleting).length

  return (
    <StyledDrawer
      onClose={() => setChildDrawerVisible(false)}
      open={childDrawerVisible}
      className="childDrawer"
      small={1}
      title={
        <Popover
          overlayClassName="calendarPopover OPEN"
          zIndex={10001}
          placement="bottom"
          content={
            <StyledPopOverTagContent>
              <StyledAppointmentTag className="styled_OPEN ant-tag tooltipTag">
                <Descriptions bordered column={{ xxl: 2, xs: 1 }} size="small">
                  {customTypes.map(type => (
                    <Descriptions.Item key={type} label={t(`common.${type.toLowerCase()}`)}>
                      {customFieldsPlaceHolder(type, t)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </StyledAppointmentTag>
            </StyledPopOverTagContent>
          }
        >
          {t('settings.groups.customFields')} <QuestionCircleTwoTone twoToneColor={mainTwoToneColor} className="scaleIfRTL" />
        </Popover>
      }
      footer={
        <div className="flex-container justify-between">
          <span />
          <Button
            onClick={handleSubmit}
            loading={loading}
            disabled={isDeleting || !user.permissions.editCompanyFields}
            htmlType="submit"
            type="primary"
          >
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <Typography.Text type="secondary">{t('common.fieldsHelp')}</Typography.Text>

      <Spin spinning={fieldsLoading || loading || isDeleting}>
        <Form
          onFinish={handleSubmit}
          name="CustomFieldsDrawer"
          form={form}
          layout="vertical"
          scrollToFirstError
          initialValues={{
            fields: fieldsData.companyCustomFields?.length ? fieldsData.companyCustomFields : undefined
          }}
        >
          <Form.List name="fields">
            {(fields, { add, remove }) => (
              <>
                <ConfirmDeleteModal
                  formVisible={formVisible}
                  setFormVisible={setFormVisible}
                  fieldName={staticFieldName}
                  title={t('common.deleteField')}
                  handleSubmit={removeField(staticKey, remove)}
                />
                {fields.map((field, index) => (
                  <Row type="flex" key={field.name} gutter={3} align="middle">
                    <Col md={9} xs={8}>
                      <Form.Item
                        hasFeedback
                        name={[index, 'name']}
                        rules={[
                          { required: true, message: t('common.nameRequired'), whitespace: true },
                          { max: 50, message: t('auth.nameLong') }
                        ]}
                        label={t('common.fieldName')}
                      >
                        <Input autoComplete="off" autoFocus={!getFieldValue(['fields', field.name, 'id'])} />
                      </Form.Item>
                    </Col>
                    <Col md={9} xs={8}>
                      <Form.Item
                        hasFeedback
                        name={[index, 'type']}
                        rules={[{ required: true, message: t('common.typeRequired') }]}
                        label={t('common.type')}
                      >
                        <Select
                          className="fixArrowSelection"
                          disabled={!!getFieldValue(['fields', field.name, 'id'])}
                          showAction={['click', isMobile ? '' : 'focus']}
                        >
                          {customTypes.map(type => (
                            <Select.Option value={type} key={type}>
                              {t(`common.${type.toLowerCase()}`)}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col md={4} xs={6} className="center">
                      <Form.Item label=" " name={[index, 'required']} valuePropName="checked">
                        <Checkbox>{t('common.required')}</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col xs={2}>
                      <Form.Item label=" ">
                        <Button
                          icon={<DeleteOutlined />}
                          type="primary"
                          danger
                          disabled={!user.permissions.editCompanyFields}
                          loading={!!deleting[field.name]}
                          onClick={removeField(field.name, remove)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  disabled={fields.length > 19 || loading || isDeleting || !user.permissions.editCompanyFields}
                  ghost
                  onClick={add}
                >
                  {t('common.addField')}
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

CustomFieldsDrawer.propTypes = {
  childDrawerVisible: PropTypes.bool.isRequired,
  setChildDrawerVisible: PropTypes.func.isRequired,
  parent: PropTypes.string.isRequired,
  fieldsData: PropTypes.any.isRequired,
  fieldsLoading: PropTypes.bool.isRequired,
  parentSetFields: PropTypes.func.isRequired
}

export default CustomFieldsDrawer
